import { useEffect, useState } from 'react';
import type { AjustesBuzon } from '../../api/ops/buzon';
import type { Operaciones } from '../../api/operaciones';
import { avisoBreveDeError } from '../../logica/estados';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Deshacer } from '../../componentes/Deshacer';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { borradorDe, cambioDe, erroresAjustes, sinCambios, type BorradorAjustes } from './reglas';

/**
 * «Ajustes del buzón» (11.3, `canales.buzon.configurar`): horas sin actividad para volver al asistente (caducidad del
 * modo humano, 1–168; G446, G689: se cambia aquí y 11.5 la enseña; sin el bloque Asistente IA no sale y se guarda la que
 * hay), horas sin mensajes para cerrarla (1–168), email si nadie responde (5–1.440 minutos) y meses de historial (1–60,
 * 12 por defecto, G204). Bajar los meses pide confirmación. Se guarda con «Deshacer» 5 s (G159) e `If-Match`.
 */
export function AjustesDelBuzon({ abierto, onCerrar, ops, negocio, movil, soloLectura, asistente }: { abierto: boolean; onCerrar: () => void; ops: Operaciones; negocio: string; movil: boolean; soloLectura: boolean; asistente: boolean }) {
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [leido, setLeido] = useState<{ datos: AjustesBuzon; version: string } | null>(null);
    const [error, setError] = useState(false);
    const [borrador, setBorrador] = useState<BorradorAjustes | null>(null);
    const [confirmar, setConfirmar] = useState(false);

    useEffect(() => {
        if (!abierto) {
            return;
        }
        let vivo = true;
        ops.getInboxSettings(negocio)
            .then((r) => {
                if (vivo) {
                    setError(false);
                    setLeido({ datos: r.datos, version: r.version ?? '' });
                    setBorrador(borradorDe(r.datos));
                }
            })
            .catch(() => { if (vivo) setError(true); });
        return () => { vivo = false; };
    }, [abierto, ops, negocio]);

    const errores = borrador === null ? null : erroresAjustes(borrador);
    const malos = errores !== null && ((asistente && errores.vuelta !== '') || errores.cierre !== '' || errores.espera !== '' || errores.meses !== '');
    const iguales = borrador === null || leido === null || sinCambios(borrador, leido.datos);

    const guardar = () => {
        if (borrador === null || leido === null) {
            return;
        }
        const anterior = leido;
        const cambio = cambioDe(borrador);
        setConfirmar(false);
        setLeido({ datos: { ...anterior.datos, ...cambio }, version: anterior.version });
        envio.programar({
            texto: 'Ajustes guardados.',
            donde: 'ajustes',
            enviar: () => {
                ops.updateInboxSettings(negocio, cambio, anterior.version)
                    .then((r) => { setLeido({ datos: r.datos, version: r.version ?? anterior.version }); })
                    .catch((e: unknown) => {
                        setLeido(anterior);
                        setBorrador(borradorDe(anterior.datos));
                        const breve = avisoBreveDeError(e);
                        avisar({
                            tipo: 'error',
                            texto: breve ?? (e instanceof ErrorApi && e.estado === 412 ? 'Otra persona ha cambiado estos ajustes. Vuelve a abrirlos.' : e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido guardar los ajustes. Prueba de nuevo.'),
                        });
                    });
            },
            alDeshacer: () => {
                setLeido(anterior);
                setBorrador(borradorDe(anterior.datos));
            },
        });
    };

    const pedirGuardar = () => {
        if (borrador === null || leido === null || malos) {
            return;
        }
        if (Number(borrador.meses) < leido.datos.retention_months) {
            setConfirmar(true);
            return;
        }
        guardar();
    };

    const cambiar = (campo: keyof BorradorAjustes) => (v: string) => { setBorrador((b) => (b === null ? b : { ...b, [campo]: v })); };
    const bajan = borrador !== null && leido !== null && errores?.meses === '' && Number(borrador.meses) < leido.datos.retention_months;

    return (
        <PanelLateral
            abierto={abierto}
            onCerrar={onCerrar}
            movil={movil}
            titulo="Ajustes del buzón"
            eyebrow="Buzón"
            icono="sliders-horizontal"
            accion={soloLectura ? '' : 'Guardar'}
            accionIcono="check"
            inactiva={malos || iguales}
            onAccion={pedirGuardar}
        >
            {envio.pendiente?.donde === 'ajustes' && <Deshacer texto={envio.pendiente.texto} onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} fondo="suave" movil={movil} />}
            {error && <p className="rv-bz-nota rv-bz-nota--error" role="alert"><Icono nombre="circle-alert" /><span>No he podido cargar los ajustes. Prueba de nuevo.</span></p>}
            {borrador !== null && errores !== null && (
                <>
                    <div className="rv-ca-bloque">
                        <h3 className="rv-bz-ceja">Atención de una persona</h3>
                        {asistente && <Campo etiqueta="Horas sin actividad para volver al asistente" tipo="number" valor={borrador.vuelta} onChange={cambiar('vuelta')} bloqueado={soloLectura} error={errores.vuelta || null} ayuda="De 1 a 168. Las fijadas no vuelven solas." />}
                        <Campo etiqueta="Horas sin mensajes para cerrarla" tipo="number" valor={borrador.cierre} onChange={cambiar('cierre')} bloqueado={soloLectura} error={errores.cierre || null} ayuda="De 1 a 168. Se reabre si el cliente escribe." />
                        <Interruptor etiqueta="Email si nadie responde" ayuda="Al propietario, con el enlace a la conversación." valor={borrador.emailOn} desactivado={soloLectura} onChange={(v) => { setBorrador((b) => (b === null ? b : { ...b, emailOn: v })); }} />
                        {borrador.emailOn && <Campo etiqueta="Minutos de espera" tipo="number" valor={borrador.espera} onChange={cambiar('espera')} bloqueado={soloLectura} error={errores.espera || null} ayuda="De 5 a 1.440." />}
                    </div>
                    <div className="rv-ca-bloque">
                        <h3 className="rv-bz-ceja">Historial</h3>
                        <Campo etiqueta="Meses que se guardan los mensajes" tipo="number" valor={borrador.meses} onChange={cambiar('meses')} bloqueado={soloLectura} error={errores.meses || null} ayuda="De 1 a 60. Después se borra su texto." />
                        {bajan && <p className="rv-bz-nota rv-bz-nota--aviso" role="note"><Icono nombre="triangle-alert" /><span>Con menos meses, en la próxima purga se borrará el texto de los mensajes más antiguos.</span></p>}
                    </div>
                    {soloLectura && <p className="rv-bz-nota"><Icono nombre="lock" /><span>El negocio está en solo lectura: puedes ver los ajustes, pero no cambiarlos.</span></p>}
                </>
            )}
            <Ventana
                abierta={confirmar}
                titulo="Guardar menos meses"
                texto={`Con ${borrador?.meses ?? ''} meses, en la próxima purga se borrará el texto de los mensajes más antiguos. No se puede recuperar.`}
                accion="Guardar"
                accionIcono="check"
                movil={movil}
                onAccion={guardar}
                onCerrar={() => { setConfirmar(false); }}
            />
        </PanelLateral>
    );
}
